import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSites, createSite, createStudio } from '@/lib/api';
import { Building2, MapPin, DoorOpen, Loader2, ArrowRight, Plus, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export default function SitesPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [addStudioSite, setAddStudioSite] = useState(null);
  const navigate = useNavigate();

  const loadSites = () => { getSites().then(setSites).catch(console.error).finally(() => setLoading(false)); };
  useEffect(() => { loadSites(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="sites-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Sites</h1>
          <p className="text-sm text-[#5C5C55] mt-1">{sites.length} location{sites.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={() => setShowCreateSite(true)}
          className="bg-[#3A4035] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2B3027]"
          data-testid="add-site-button">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Add Site
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {sites.map((site, i) => (
          <div key={site.site_id}
            className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 animate-fade-in stagger-${i + 1}`}
            data-testid={`site-card-${site.site_id}`}>
            <button onClick={() => navigate(`/studios?site=${site.site_id}`)} className="w-full p-6 sm:p-8 text-left">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[#E8EDE5] flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#3A4035]" strokeWidth={1.5} />
                </div>
                <ArrowRight className="w-5 h-5 text-[#8A8B85] mt-1" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-xl font-medium text-[#1A1A1A] mb-2">{site.name}</h2>
              <div className="flex items-center gap-1.5 text-sm text-[#5C5C55] mb-3">
                <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
                {site.address}{site.city ? `, ${site.city}` : ''} {site.postcode}
              </div>
              {site.description && <p className="text-sm text-[#8A8B85] mb-4 line-clamp-2">{site.description}</p>}
              <div className="flex items-center gap-2">
                <DoorOpen className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
                <span className="text-sm font-medium text-[#5C5C55]">{site.total_studios} studio{site.total_studios !== 1 ? 's' : ''}</span>
              </div>
            </button>
            <div className="px-6 sm:px-8 pb-5 pt-0">
              <button onClick={(e) => { e.stopPropagation(); setAddStudioSite(site); }}
                className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-[#D8DBD3] text-xs font-medium text-[#5C5C55] hover:border-[#3A4035] hover:text-[#3A4035] hover:bg-[#FAF9F6] transition-all"
                data-testid={`add-studio-in-site-${site.site_id}`}>
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />Add Studio to {site.name}
              </button>
            </div>
          </div>
        ))}
      </div>

      {sites.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-[#F2F0EB] flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-[#D8DBD3]" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-2">No sites yet</h3>
          <p className="text-sm text-[#8A8B85] mb-6 max-w-xs mx-auto">Create your first site to start adding studios.</p>
          <Button onClick={() => setShowCreateSite(true)}
            className="bg-[#3A4035] text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-[#2B3027]"
            data-testid="empty-add-site-button">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Add Your First Site
          </Button>
        </div>
      )}

      <CreateSiteModal open={showCreateSite} onClose={() => setShowCreateSite(false)} onCreated={() => { setShowCreateSite(false); loadSites(); }} />

      {addStudioSite && (
        <CreateStudioModal open={!!addStudioSite} onClose={() => setAddStudioSite(null)} sites={sites} defaultSite={addStudioSite.site_id}
          onCreated={() => { setAddStudioSite(null); loadSites(); }} onNeedSite={() => { setAddStudioSite(null); setShowCreateSite(true); }} />
      )}
    </div>
  );
}

// ── Create Site Modal ──
function CreateSiteModal({ open, onClose, onCreated }) {
  const emptyForm = { name: '', address: '', city: '', postcode: '', description: '' };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({});

  // Reset form every time modal opens
  useEffect(() => {
    if (open) {
      setForm({ name: '', address: '', city: '', postcode: '', description: '' });
      setSaving(false);
      setError('');
      setTouched({});
    }
  }, [open]);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setTouched(t => ({ ...t, [k]: true }));
    setError('');
  };

  const canSubmit = form.name.trim() && form.address.trim();

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setTouched({ name: true, address: true });
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      await createSite({
        name: form.name.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        postcode: form.postcode.trim(),
        description: form.description.trim(),
        total_studios: 0,
      });
      onCreated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(d => d.msg || '').join('. '));
      else setError('Failed to create site. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (key) => touched[key] && !form[key].trim();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="create-site-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Add Site</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8B85]">Sites are your studio locations. Add a site first, then create studios within it.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]" data-testid="site-form-error">
            <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-[#B33A3A]">{error}</p>
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4 pt-1">
          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">
              Site Name <span className="text-[#B33A3A]">*</span>
            </Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. The Crescent Studios"
              className={`mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10 ${fieldError('name') ? 'border-[#B33A3A] bg-[#FDF8F8]' : ''}`}
              data-testid="site-name-input" autoFocus />
            {fieldError('name') && <p className="text-[10px] text-[#B33A3A] mt-1" data-testid="name-error">Site name is required</p>}
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">
              Address <span className="text-[#B33A3A]">*</span>
            </Label>
            <Input value={form.address} onChange={e => set('address', e.target.value)}
              placeholder="14 The Crescent"
              className={`mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10 ${fieldError('address') ? 'border-[#B33A3A] bg-[#FDF8F8]' : ''}`}
              data-testid="site-address-input" />
            {fieldError('address') && <p className="text-[10px] text-[#B33A3A] mt-1" data-testid="address-error">Address is required</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">City</Label>
              <Input value={form.city} onChange={e => set('city', e.target.value)}
                placeholder="Manchester"
                className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10"
                data-testid="site-city-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Postcode</Label>
              <Input value={form.postcode} onChange={e => set('postcode', e.target.value)}
                placeholder="M1 4BT"
                className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10"
                data-testid="site-postcode-input" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Description (optional)</Label>
            <Textarea value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Brief description of the site" rows={2}
              className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl resize-none"
              data-testid="site-description-input" />
          </div>

          <Button type="submit" disabled={saving || !canSubmit}
            className={`w-full rounded-full h-11 text-sm font-medium transition-all ${canSubmit && !saving ? 'bg-[#3A4035] text-white hover:bg-[#2B3027]' : 'bg-[#E5E2DC] text-[#8A8B85] cursor-not-allowed'}`}
            data-testid="save-site-button">
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating...</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Site</>
            )}
          </Button>

          {!canSubmit && (touched.name || touched.address) && (
            <p className="text-[10px] text-[#8A8B85] text-center" data-testid="validation-hint">
              Fill in the required fields marked with <span className="text-[#B33A3A]">*</span> to continue
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Create Studio Modal ──
function CreateStudioModal({ open, onClose, sites, defaultSite, onCreated, onNeedSite }) {
  const [form, setForm] = useState({ site_id: defaultSite || '', name: '', floor: '', monthly_rent: '', description: '', size_sqft: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setError(''); };

  useEffect(() => { if (defaultSite) set('site_id', defaultSite); }, [defaultSite]);
  useEffect(() => { if (open) { setForm({ site_id: defaultSite || '', name: '', floor: '', monthly_rent: '', description: '', size_sqft: '' }); setError(''); setSaving(false); } }, [open, defaultSite]);

  const canSubmit = form.site_id && form.name.trim() && form.monthly_rent;

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      await createStudio({ ...form, name: form.name.trim(), monthly_rent: parseFloat(form.monthly_rent) || 0, size_sqft: parseInt(form.size_sqft) || 0 });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create studio.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="create-studio-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Add Studio</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8B85]">Add a new studio to a site</DialogDescription>
        </DialogHeader>
        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]">
            <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-[#B33A3A]">{typeof error === 'string' ? error : 'An error occurred'}</p>
          </div>
        )}
        {sites.length === 0 ? (
          <div className="bg-[#FDFAF0] rounded-xl p-4 border border-[#EDE4C8]" data-testid="no-sites-warning">
            <p className="text-sm font-medium text-[#8A7230] mb-1">No sites found</p>
            <p className="text-xs text-[#8A7230]/70 mb-3">You need to create a site before adding studios.</p>
            <Button onClick={() => { onClose(); if (onNeedSite) onNeedSite(); }} className="bg-[#3A4035] text-white rounded-full px-4 py-2 text-xs hover:bg-[#2B3027]" data-testid="create-site-from-studio">
              <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Add Site First
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4 pt-1">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Site <span className="text-[#B33A3A]">*</span></Label>
              <Select value={form.site_id} onValueChange={v => set('site_id', v)}>
                <SelectTrigger className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Studio Name <span className="text-[#B33A3A]">*</span></Label>
                <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Studio 8" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-name-input" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Floor</Label>
                <Input value={form.floor} onChange={e => set('floor', e.target.value)} placeholder="e.g. Ground Floor" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-floor-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Monthly Rent (£) <span className="text-[#B33A3A]">*</span></Label>
                <Input type="number" value={form.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} placeholder="650" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-rent-input" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Size (sq ft)</Label>
                <Input type="number" value={form.size_sqft} onChange={e => set('size_sqft', e.target.value)} placeholder="150" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-size-input" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Description (optional)</Label>
              <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description" rows={2} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl resize-none" data-testid="studio-description-input" />
            </div>
            <Button type="submit" disabled={saving || !canSubmit}
              className={`w-full rounded-full h-11 text-sm font-medium ${canSubmit && !saving ? 'bg-[#3A4035] text-white hover:bg-[#2B3027]' : 'bg-[#E5E2DC] text-[#8A8B85] cursor-not-allowed'}`}
              data-testid="save-studio-button">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating...</> : <><Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Studio</>}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
